import { db } from '../firebase';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp, collection, query, where, orderBy, getDocs, addDoc, limit } from 'firebase/firestore';

const STREAK_COLLECTION = 'streaks';
const ACTIVITY_COLLECTION = 'activities';
const STUDY_HISTORY_COLLECTION = 'studyHistory';

export const streakService = {
  async getStreak(userId) {
    try {
      const streakDoc = await getDoc(doc(db, STREAK_COLLECTION, userId));
      if (!streakDoc.exists()) {
        // Initialize streak data if it doesn't exist
        const initialStreak = {
          current: 0,
          longest: 0,
          lastActivity: null,
          todayProgress: 0,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };
        await setDoc(doc(db, STREAK_COLLECTION, userId), initialStreak);
        return initialStreak;
      }
      return streakDoc.data();
    } catch (error) {
      console.error('Error getting streak:', error);
      throw error;
    }
  },

  async updateStreak(userId, activity) {
    try {
      const streakData = await this.getStreak(userId);
      const updatedStreak = {
        ...streakData,
        lastActivity: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await setDoc(doc(db, STREAK_COLLECTION, userId), updatedStreak);
      await logActivity(userId, activity);

      return updatedStreak;
    } catch (error) {
      console.error('Error updating streak:', error);
      throw error;
    }
  },

  async getTodayProgress(userId) {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const activities = await getDoc(doc(db, ACTIVITY_COLLECTION, userId));
      if (!activities.exists()) return 0;

      const todayActivities = activities.data().activities.filter(activity => 
        activity.timestamp.toDate() >= today
      );

      return calculateTodayProgress({ activities: todayActivities });
    } catch (error) {
      console.error('Error getting today progress:', error);
      throw error;
    }
  },

  async recordDailyVisit(userId) {
    try {
      // Check if we already have a visit for today
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const studyHistoryRef = collection(db, STUDY_HISTORY_COLLECTION);
      const todayQuery = query(
        studyHistoryRef,
        where('userId', '==', userId),
        where('type', '==', 'daily_visit'),
        where('timestamp', '>=', today),
        orderBy('timestamp', 'desc'),
        limit(1)
      );

      const todayVisits = await getDocs(todayQuery);
      
      // Only add new visit record if we don't have one for today
      if (todayVisits.empty) {
        await addDoc(studyHistoryRef, {
          userId,
          type: 'daily_visit',
          timestamp: serverTimestamp()
        });
      }

      // Update streak
      const streak = await this.calculateDailyStreak(userId);
      await this.updateStreak(userId, { type: 'DAILY_VISIT' });

      return streak;
    } catch (error) {
      console.error('Error recording daily visit:', error);
      throw error;
    }
  },

  async calculateDailyStreak(userId) {
    try {
      const studyHistoryRef = collection(db, STUDY_HISTORY_COLLECTION);
      const q = query(
        studyHistoryRef,
        where('userId', '==', userId),
        where('type', '==', 'daily_visit'),
        orderBy('timestamp', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const visits = querySnapshot.docs.map(doc => doc.data().timestamp?.toDate());

      if (visits.length === 0) return 1; // First visit

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const lastVisit = visits[0];
      if (!lastVisit) return 1;

      const streakDoc = await getDoc(doc(db, STREAK_COLLECTION, userId));
      const currentStreak = streakDoc.exists() ? streakDoc.data().current : 0;

      lastVisit.setHours(0, 0, 0, 0);

      if (isSameDay(lastVisit, today)) {
        return currentStreak || 1;
      }

      if (isDayBefore(lastVisit, today)) {
        return (currentStreak || 0) + 1;
      }

      return 1; // Streak broken
    } catch (error) {
      console.error('Error calculating daily streak:', error);
      throw error;
    }
  },
};

// Helper functions
function isSameDay(date1, date2) {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

function isDayBefore(date1, date2) {
  const day1 = new Date(date1);
  const day2 = new Date(date2);
  day1.setHours(0, 0, 0, 0);
  day2.setHours(0, 0, 0, 0);
  const diffTime = Math.abs(day2 - day1);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays === 1;
}

function calculateTodayProgress(activity) {
  // Define point values for different activities
  const POINTS = {
    QUESTION_ANALYZED: 20,
    WRONG_ANSWER_ADDED: 10,
    PRACTICE_COMPLETED: 30,
    REVIEW_COMPLETED: 15,
  };

  // Calculate progress based on activity type and count
  let progress = 0;
  const targetPoints = 100; // Points needed for 100% progress

  if (activity.type === 'QUESTION_ANALYZED') {
    progress += POINTS.QUESTION_ANALYZED;
  } else if (activity.type === 'WRONG_ANSWER_ADDED') {
    progress += POINTS.WRONG_ANSWER_ADDED;
  } else if (activity.type === 'PRACTICE_COMPLETED') {
    progress += POINTS.PRACTICE_COMPLETED;
  } else if (activity.type === 'REVIEW_COMPLETED') {
    progress += POINTS.REVIEW_COMPLETED;
  }

  // Cap progress at 100%
  return Math.min(Math.round((progress / targetPoints) * 100), 100);
}

async function logActivity(userId, activity) {
  try {
    const activityDoc = await getDoc(doc(db, ACTIVITY_COLLECTION, userId));
    const activities = activityDoc.exists() ? activityDoc.data().activities : [];

    // Create a new activity with current timestamp
    const newActivity = {
      ...activity,
      timestamp: new Date().toISOString() // Use ISO string instead of serverTimestamp
    };

    activities.push(newActivity);

    // Keep only last 100 activities
    if (activities.length > 100) {
      activities.shift();
    }

    // Update the document with the new activities array and use serverTimestamp for the document's lastUpdated field
    await setDoc(doc(db, ACTIVITY_COLLECTION, userId), {
      activities,
      lastUpdated: serverTimestamp() // Use serverTimestamp only at the document level
    });
  } catch (error) {
    console.error('Error logging activity:', error);
    throw error;
  }
} 